import React from 'react';
import * as d3 from 'd3';
import { useI18n } from '@whizz/react-i18n';
import type { HighlightedProps, SchoolReportType } from '../../types';
import type { DateRange } from '../../../util/types';
import { SectionLayout } from '../modules';
import { StudentProgress } from '../../../components';
import { convertWeeksToYears } from '../../../util/numbers';
import type { ChartDataType, ChartIconType } from '../../../components/charts/types';
import { DateTime, Interval } from 'luxon';

type Props = {
  schoolReport: SchoolReportType;
  showOthersData: boolean
  iconToUse: ChartIconType
  dateRange?: DateRange
} & HighlightedProps;

const YEAR_IN_WEEKS = 52;

const SchoolMathsAgeImprovements = ({ schoolReport, showOthersData, highlightedLabel, highlightedIds, iconToUse, dateRange, onElementClick }: Props): JSX.Element => {
  const { translate } = useI18n();

  const value = schoolReport.average.mathsAgeImprovementAverage.toFixed(1);

  const dataToUse = (): ChartDataType[] => {
    if (iconToUse === 'class') {
      return schoolReport.classes;
    } else return schoolReport.yearGroups;
  };

  const getHeaderValue = (groups: ChartDataType[]): number => {
    let groupList = groups;
    if (highlightedIds.length > 0) {
      groupList = groupList.filter((group) => highlightedIds.includes(group.id));
    }
    const mean = d3.mean(groupList.map((group) => group.chartsData?.mathsAgeImprovement));
    return mean != undefined ? mean : 0;
  };

  let allGroups = dataToUse();
  let unit = 'unitWeek';
  let highlightValue = getHeaderValue(allGroups);
  /**
   * If the value (number of weeks) is greater than 52 (1 year) convert value and units to 'year'
   */
  if (!showOthersData && highlightValue > YEAR_IN_WEEKS) {
    unit = 'unitYear';

    // Convert the `mathsAgeImprovement` to years
    allGroups = allGroups.map((group) => {
      return ({
        ...group,
        chartsData: {
          ...group.chartsData,
          mathsAgeImprovement: group.chartsData?.mathsAgeImprovement ? convertWeeksToYears(group.chartsData?.mathsAgeImprovement) : 0,
        },
      });
    }) as ChartDataType[];
    highlightValue = convertWeeksToYears(highlightValue);
  }

  highlightValue = Number(highlightValue.toFixed(2));

  const headerValue = Number((Number(value) > YEAR_IN_WEEKS ? convertWeeksToYears(Number(value)) : value)).toFixed(2);

  // Calculate the number of weeks in the date range
  const dateRangeInterval = (dateRange && DateTime.isDateTime(dateRange.startDate!)) ? Interval.fromDateTimes(dateRange.startDate!, dateRange.endDate ?? DateTime.now()) : null;
  const numberOfWeeks = dateRangeInterval ? Math.round(dateRangeInterval?.toDuration('weeks').weeks) : null;

  return (
    <SectionLayout
      title={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.title')}
      value={showOthersData ? headerValue : null}
      highlightLabel={highlightedLabel}
      highlightValue={highlightValue}
      unit={translate(`pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.${unit}`, { count: parseFloat(value) })}
      moreInfo={translate('pages.REPORTS.SECTIONS.MATHS_AGE_IMPROVEMENTS.info')}
      id={'school-maths-age-improvements-container'}
    >
      <StudentProgress
        showOthersData={showOthersData}
        data={allGroups}
        chartType={'mathsAgeImprovement'}
        type={iconToUse}
        highlightedIds={highlightedIds}
        excellentBreaking={numberOfWeeks}
        thinAxis={numberOfWeeks === null}
        onElementClick={onElementClick}
      />
    </SectionLayout>
  );
};

SchoolMathsAgeImprovements.displayName = 'SchoolMathsAgeImprovements';

export default SchoolMathsAgeImprovements;
